import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import React from 'react'
import Directory, {
  IDirectoryBreadcrumb,
  IDirectoryISection
} from 'src/components/Directory'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
import {
  HOOD_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchPageCity
} from 'src/constants/searchPages'
import { RedirectUrls } from 'src/types/types'
const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})

const image = 'https://www.spotme.mx/images/cover.png'

type IProps = {
  pillar: PillarPage | null
  city: SearchPageCity | null
}

const Page: NextPage<IProps> = ({ pillar, city }) => {
  const { t } = useTranslation('common')
  const titleDir = pillar
    ? PILLAR_PAGE_MAP[pillar].label
    : city
      ? SEARCH_PAGE_CITY_MAP[city].cityName
      : 'Directorio'

  const pageTitle = `SpotMe | Directorio de ${titleDir}`
  const pageDescription = pillar
    ? `Encuentra ${PILLAR_PAGE_MAP[pillar].searchName.toLowerCase()} en todas las ciudades de México. Directorio completo de opciones disponibles en SpotMe.`
    : city
      ? `Todos los espacios disponibles en ${SEARCH_PAGE_CITY_MAP[city].cityName}: mini-bodegas, estacionamientos, bodegas y naves industriales en renta.`
      : 'Directorio de espacios en renta en SpotMe.'
  const pageUrl = `https://www.spotme.mx${RedirectUrls.Directory}/${city ?? pillar ?? ''}`

  const sections: IDirectoryISection[] = []

  if (pillar) {
    sections.push({
      title: `${PILLAR_PAGE_MAP[pillar].label} por Ciudad`,
      description: `Encuentra ${PILLAR_PAGE_MAP[pillar].searchName.toLowerCase()} en las principales ciudades de México`,
      items: Object.values(SearchPageCity)
        .filter((c) => c)
        .sort()
        .map((c) => ({
          label: SEARCH_PAGE_CITY_MAP[c].cityName || 'Todas',
          url: `/${pillar}/${c}`
        }))
    })

    const citiesWithHoods = Object.values(SearchPageCity)
      .filter((c) => c && SEARCH_PAGE_CITY_MAP[c].hoods?.length)
      .sort()

    for (const c of citiesWithHoods) {
      sections.push({
        title: `${PILLAR_PAGE_MAP[pillar].label} en ${SEARCH_PAGE_CITY_MAP[c].cityName} por Colonia`,
        items:
          SEARCH_PAGE_CITY_MAP[c].hoods?.sort().map((hood) => ({
            label: HOOD_MAP[hood].hoodName,
            url: `/${pillar}/${c}/${hood}`
          })) ?? []
      })
    }
  }

  if (city) {
    sections.push({
      title: 'Tipos de Espacio',
      description: `Todos los tipos de espacio disponibles en ${SEARCH_PAGE_CITY_MAP[city].cityName}`,
      items: Object.values(PillarPage)
        .sort()
        .map((p) => ({
          label: PILLAR_PAGE_MAP[p].label,
          url: `/${p}/${city}`
        }))
    })

    const hoods = SEARCH_PAGE_CITY_MAP[city].hoods
    if (hoods?.length) {
      sections.push({
        title: `Mini-Bodegas por Colonia en ${SEARCH_PAGE_CITY_MAP[city].cityName}`,
        items: hoods.sort().map((hood) => ({
          label: HOOD_MAP[hood].hoodName,
          url: `/${PillarPage.SelfStorage}/${city}/${hood}`
        }))
      })
      sections.push({
        title: `Estacionamientos por Colonia en ${SEARCH_PAGE_CITY_MAP[city].cityName}`,
        items: hoods.sort().map((hood) => ({
          label: HOOD_MAP[hood].hoodName,
          url: `/${PillarPage.Parking}/${city}/${hood}`
        }))
      })
      sections.push({
        title: `Bodegas Comerciales por Colonia en ${SEARCH_PAGE_CITY_MAP[city].cityName}`,
        items: hoods.sort().map((hood) => ({
          label: HOOD_MAP[hood].hoodName,
          url: `/${PillarPage.Warehouse}/${city}/${hood}`
        }))
      })
      sections.push({
        title: `Naves Industriales por Colonia en ${SEARCH_PAGE_CITY_MAP[city].cityName}`,
        items: hoods.sort().map((hood) => ({
          label: HOOD_MAP[hood].hoodName,
          url: `/${PillarPage.Industrial}/${city}/${hood}`
        }))
      })
    }
  }

  const breadcrumbs: IDirectoryBreadcrumb[] = [
    {
      title: titleDir,
      url: `${RedirectUrls.Directory}/${city ?? pillar ?? ''}`
    }
  ]

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={pageDescription}
        canonical={pageUrl}
        openGraph={{
          type: 'website',
          url: pageUrl,
          title: pageTitle,
          description: pageDescription,
          images: [{ url: image, width: 1200, height: 630 }]
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'SpotMe', item: 'https://www.spotme.mx' },
          {
            position: 2,
            name: 'Directorio',
            item: 'https://www.spotme.mx/directory'
          },
          { position: 3, name: titleDir, item: pageUrl }
        ]}
      />

      <main>
        <LandingNav />
        <Directory
          titleDir={titleDir}
          subtitle={pageDescription}
          sections={sections}
          breadcrumbs={breadcrumbs}
        />
        <Footer light />

        <GooglePopup />
        <WhatsAppButton msg={t('WHATSAPP_DIRECTORY_MSG')} />
      </main>
    </>
  )
}

export default React.memo(Page)

export type IParams = {
  param: PillarPage | SearchPageCity
}

export const getStaticPaths = (async ({ locales = [] }) => {
  const pillar: { params: IParams }[] = Object.values(PillarPage).map(
    (param) => ({ params: { param } })
  )
  const city: { params: IParams }[] = Object.values(SearchPageCity)
    .filter((c) => c)
    .map((param) => ({ params: { param } }))
  const paths = [...pillar, ...city]
    .map(({ params }) => locales.map((locale) => ({ params, locale })).flat())
    .flat()
  return { paths, fallback: false }
}) satisfies GetStaticPaths

export const getStaticProps: GetStaticProps<IProps> = async (context) => {
  const { params, locale } = context
  const { param } = params as IParams
  const translations = await serverSideTranslations(locale ?? 'es', [
    'common',
    'footer',
    'nav',
    'platform_stats',
    'auth'
  ])
  const pillar = Object.values(PillarPage).includes(param as PillarPage)
    ? (param as PillarPage)
    : null
  const city = Object.values(SearchPageCity).includes(param as SearchPageCity)
    ? (param as SearchPageCity)
    : null
  return { props: { pillar, city, ...translations } }
}
